import React, { createContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { UserProfile, ScopePermissions, UserScope } from '../types/auth.types';
import { UsersService } from '../services/users.service';
import { useAuth } from '../hooks/useAuth';
import { defaultPermissions, calculatePermissions } from '../utils/permissions.utils';
import { useErrorHandler } from '@shared/hooks/useErrorHandler';

interface UserProfileContextType {
  userProfile: UserProfile | null;
  permissions: ScopePermissions;
  isLoading: boolean;
  error: string | null;
  hasScope: (scope: UserScope) => boolean;
  hasAnyScope: (scopes: UserScope[]) => boolean;
  hasAllScopes: (scopes: UserScope[]) => boolean;
  refreshProfile: () => Promise<void>;
}

const UserProfileContext = createContext<UserProfileContextType | undefined>(undefined);

export { UserProfileContext };

export const UserProfileProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [permissions, setPermissions] = useState<ScopePermissions>(defaultPermissions);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { handleError } = useErrorHandler();
  
  // Ref para evitar múltiples requests simultáneos
  const isLoadingRef = useRef(false);
  // Ref para mantener handleError estable
  const handleErrorRef = useRef(handleError);
  
  // Actualizar el ref cuando handleError cambia
  useEffect(() => {
    handleErrorRef.current = handleError;
  }, [handleError]);

  const loadUserProfile = useCallback(async () => {
    if (authLoading) {
      return;
    }

    if (!isAuthenticated) {
      setUserProfile(null);
      setPermissions(defaultPermissions);
      setIsLoading(false);
      isLoadingRef.current = false;
      return;
    }

    if (isLoadingRef.current) {
      return;
    }

    isLoadingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const profile = await UsersService.getUserProfile();
      if (profile) {
        setUserProfile(profile);
        setPermissions(calculatePermissions(profile.scopes));
      } else {
        setError('No se pudo cargar el perfil del usuario');
      }
    } catch (err) {
      setError('Error al cargar el perfil del usuario');
      handleErrorRef.current(err, { showNotification: false, logToConsole: true });
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  }, [isAuthenticated, authLoading]);

  const hasScope = (scope: UserScope): boolean => {
    return userProfile?.scopes.includes(scope) ?? false;
  };

  const hasAnyScope = (scopes: UserScope[]): boolean => {
    return scopes.some(scope => hasScope(scope));
  };

  const hasAllScopes = (scopes: UserScope[]): boolean => {
    return scopes.every(scope => hasScope(scope));
  };

  const refreshProfile = useCallback(async () => {
    isLoadingRef.current = false; // Resetear el flag para permitir refresh
    await loadUserProfile();
  }, [loadUserProfile]);

  useEffect(() => {
    if (!authLoading) {
      loadUserProfile();
    }
  }, [isAuthenticated, authLoading, loadUserProfile]);

  const value: UserProfileContextType = {
    userProfile,
    permissions,
    isLoading,
    error,
    hasScope,
    hasAnyScope,
    hasAllScopes,
    refreshProfile,
  };

  return (
    <UserProfileContext.Provider value={value}>
      {children}
    </UserProfileContext.Provider>
  );
};

 